import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MdDashboard,
  MdAssessment,
  MdTrendingUp,
  MdBook,
  MdLogout,
  MdBarChart,
  MdSummarize,
} from "react-icons/md";
import logoImage from "../images/EX-logo-1.png";

interface SidebarProps {
  onToggle?: (expanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [, setRefresh] = useState(0);

  useEffect(() => {
    const orig = Storage.prototype.setItem;
    Storage.prototype.setItem = function (key: string, value: string) {
      orig.call(this, key, value);
      if (key === "dashboardFilters") {
        window.dispatchEvent(new CustomEvent("dashboardFiltersUpdated"));
      }
    };
    return () => { Storage.prototype.setItem = orig; };
  }, []);

  useEffect(() => {
    const handler = () => setRefresh((r) => r + 1);
    window.addEventListener("dashboardFiltersUpdated", handler);
    return () => window.removeEventListener("dashboardFiltersUpdated", handler);
  }, []);

  // Authentication check - redirect to login if not authenticated as Coordinator
  useEffect(() => {
    if (!sessionStorage.getItem("Coordinator")) {
      navigate('/');
    }
  }, [location, navigate]);

  const toggleSidebar = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onToggle?.(newExpandedState);
  };

  const handleReportClick = () => {
    navigate("/coordinator/reports");
  };

  const handleDashboardClick = () => {
    // Check if there are LearningProgress filters to restore
    const learningProgressFilters = sessionStorage.getItem(
      "learningProgressFilters"
    );
    if (learningProgressFilters) {
      try {
        const filters = JSON.parse(learningProgressFilters);
        // Save these filters as dashboard filters
        sessionStorage.setItem("dashboardFilters", JSON.stringify(filters));
      } catch (error) {
        console.error("Error parsing LearningProgress filters:", error);
      }
    }

    // Reports filters are already saved as dashboardFilters, so no additional action needed
    navigate("/coordinator/dashboard");
  };

  const handlePerformanceClick = () => {
    // Get current dashboard filters from session storage or use defaults
    const dashboardFilters = sessionStorage.getItem("dashboardFilters");
    let filters: { course: string; batch: string; subject?: string | null; project?: unknown } = { course: "", batch: "", subject: "" };

    if (dashboardFilters) {
      try {
        filters = JSON.parse(dashboardFilters);
      } catch (error) {
        console.error("Error parsing dashboard filters:", error);
      }
    }

    navigate("/coordinator/learning-progress", {
      state: {
        course: filters.course,
        batch: filters.batch,
        subject: filters.subject ?? null,
        project: filters.project ?? null,
        fromSidebar: true, // Flag to indicate this is from sidebar (no category)
      },
    });
  };

  const handleCourseClick = () => {
    if (isCourseOverviewDisabled) return;
    // Get current dashboard filters from session storage or use defaults
    const dashboardFilters = sessionStorage.getItem("dashboardFilters");
    let filters: { course?: string; batch?: string; subject?: string; project?: unknown } = { course: "", batch: "", subject: "" };

    if (dashboardFilters) {
      try {
        filters = JSON.parse(dashboardFilters);
      } catch (error) {
        console.error("Error parsing dashboard filters:", error);
      }
    }

    // Save filters to session storage for course overview page
    if (filters.course && filters.batch && filters.subject) {
      sessionStorage.setItem("courseOverviewFilters", JSON.stringify(filters));
    }
    
    navigate("/coordinator/course-overview");
  };

  const dashboardFilters = sessionStorage.getItem("dashboardFilters");
  let isCourseOverviewDisabled = false;
  if (dashboardFilters) {
    try {
      const filters = JSON.parse(dashboardFilters) as { project?: unknown };
      isCourseOverviewDisabled = filters?.project != null && filters?.project !== "";
    } catch {
      // ignore
    }
  }

  const handleOverallProgressClick = () => {
    navigate("/coordinator/overall-progress-report");
  };

  const handleSessionSummaryClick = () => {
    navigate("/coordinator/session-summary");
  };

  const handleLogout = () => {
    // Clear all session storage
    sessionStorage.clear();
    // Navigate to login page
    navigate("/");
  };

  return (
    <div 
      className={`sidebar ${isExpanded ? 'expanded' : 'collapsed'}`}
      onClick={toggleSidebar}
    >
      <div className="logo-section">
        <img
          src={logoImage}
          alt="Logo"
          className="logo"
        />
      </div>
      
      <div className="nav-icons">
        <div
          className={`nav-item ${
            location.pathname === "/coordinator/dashboard" ? "active" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleDashboardClick();
          }}
        >
          <MdDashboard size={24} />
          {isExpanded && <span className="nav-text">Dashboard</span>}
        </div>
        
        <div
          className={`nav-item ${
            location.pathname === "/coordinator/reports" ? "active" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleReportClick();
          }}
        >
          <MdAssessment size={24} />
          {isExpanded && <span className="nav-text">Reports</span>}
        </div>

        {/* <div
          className={`nav-item ${
            location.pathname === "/coordinator/session-summary" ? "active" : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleSessionSummaryClick();
          }}
        >
          <MdSummarize size={24} />
          {isExpanded && <span className="nav-text">Session Summary</span>}
        </div>
         */}
        <div
          className={`nav-item ${
            location.pathname === "/coordinator/learning-progress"
              ? "active"
              : ""
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handlePerformanceClick();
          }}
        >
          <MdTrendingUp size={24} />
          {isExpanded && <span className="nav-text">Learning Progress</span>}
        </div>
        
        <div
          className={`nav-item ${
            location.pathname === "/coordinator/course-overview" ? "active" : ""
          } ${isCourseOverviewDisabled ? "disabled" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            handleCourseClick();
          }}
          style={isCourseOverviewDisabled ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "auto" } : undefined}
        >
          <MdBook size={24} />
          {isExpanded && <span className="nav-text">Course Overview</span>}
        </div>
        
        {sessionStorage.getItem('SC') !== 'True' && 
        (
          <div
            className={`nav-item ${
              location.pathname === "/coordinator/overall-progress-report" || 
              location.pathname === "/coordinator/sub-batch-details" 
                ? "active" 
                : ""
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleOverallProgressClick();
            }}
          >
            <MdBarChart size={24} />
            {isExpanded && <span className="nav-text">Overall Progress</span>}
          </div>
        )}
      </div>
      
      <div className="logout-container">
        <div 
          className="nav-item logout-item" 
          onClick={(e) => {
            e.stopPropagation();
            handleLogout();
          }}
        >
          <MdLogout size={24} />
          {isExpanded && <span className="nav-text">Logout</span>}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
